import { useEffect, useMemo, useState, type DragEvent } from 'react'
import { getHealth, type Health, type DashboardWidgetPref, type DashboardWidgetWidth } from '../api'
import { useAuth } from '../layout/AuthContext'
import InfoTip from '../components/InfoTip'
import { WIDGETS, WIDGET_BY_ID, type WidgetMeta } from '../dashboard/widgets'
import { fill, PAGES_DASHBOARD_TEXTS as T } from '../../shared/texts'

// Per-user configurable dashboard. The placed widgets, their order, width and
// per-instance config live in user.preferences.dashboard. Each placed widget has a
// unique `key` (so a configurable type like the revenue chart can appear several
// times, e.g. one per timeframe) and a `type` from the registry. Which types a user
// may pick is gated by RBAC (can(widget.module,'read')).
//
// Customizing ("Anpassen") opens a drawer: single widgets toggle on/off, configurable
// widgets are added as new instances; on the board, cards drag to reorder and (for
// configurable ones) carry a small config editor.

const defaultWidth = (type: string): DashboardWidgetWidth =>
    (type === 'stats' || type === 'revenue-chart' ? 'wide' : 'narrow')

// Normalize a saved pref (legacy ones only had `id` = type = key).
function normalize(p: DashboardWidgetPref): DashboardWidgetPref {
    const type = p.type ?? p.id ?? p.key
    const key = p.key ?? p.id ?? type
    return { key, type, width: p.width ?? defaultWidth(type), config: p.config }
}

const buildDefault = (avail: WidgetMeta[]): DashboardWidgetPref[] =>
    avail.map((w) => ({ key: w.id, type: w.id, width: defaultWidth(w.id), config: w.defaultConfig }))

const newKey = (type: string) =>
    `${type}:${(globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random()))}`

// Move the dragged widget (by key) to `dropIndex` in the original list's coordinates.
function reorder(list: DashboardWidgetPref[], dragKey: string, dropIndex: number): DashboardWidgetPref[] {
    const from = list.findIndex((w) => w.key === dragKey)
    if (from < 0) return list
    const arr = [...list]
    const [moved] = arr.splice(from, 1)
    let at = from < dropIndex ? dropIndex - 1 : dropIndex
    at = Math.max(0, Math.min(at, arr.length))
    arr.splice(at, 0, moved)
    return arr
}

export default function Dashboard() {
    const { preferences, savePreferences, can } = useAuth()
    const [customizing, setCustomizing] = useState(false)
    const [dragKey, setDragKey] = useState<string | null>(null)
    const [dropIndex, setDropIndex] = useState<number | null>(null)

    const [health, setHealth] = useState<Health | null>(null)
    useEffect(() => { getHealth().then(setHealth).catch(() => setHealth(null)) }, [])

    const available = useMemo(() => WIDGETS.filter((w) => can(w.module, 'read')), [can])
    const availableTypes = useMemo(() => new Set(available.map((w) => w.id)), [available])

    // Active widgets, derived from preferences and pruned to available types.
    const widgets = useMemo<DashboardWidgetPref[]>(() => {
        const saved = preferences.dashboard?.widgets
        if (saved) return saved.map(normalize).filter((w) => availableTypes.has(w.type) && WIDGET_BY_ID[w.type])
        return buildDefault(available)
    }, [preferences.dashboard, available, availableTypes])

    const persist = (next: DashboardWidgetPref[]) => savePreferences({ dashboard: { widgets: next } })

    const setWidth = (key: string, width: DashboardWidgetWidth) =>
        persist(widgets.map((w) => (w.key === key ? { ...w, width } : w)))
    const setConfig = (key: string, config: Record<string, unknown>) =>
        persist(widgets.map((w) => (w.key === key ? { ...w, config } : w)))
    const removeWidget = (key: string) => persist(widgets.filter((w) => w.key !== key))
    const toggleSingle = (type: string) => persist(
        widgets.some((w) => w.type === type)
            ? widgets.filter((w) => w.type !== type)
            : [...widgets, { key: type, type, width: defaultWidth(type) }],
    )
    const addInstance = (meta: WidgetMeta) =>
        persist([...widgets, { key: newKey(meta.id), type: meta.id, width: defaultWidth(meta.id), config: meta.defaultConfig }])
    const reset = () => persist(buildDefault(available))

    const resetDrag = () => { setDragKey(null); setDropIndex(null) }
    const commitDrop = () => {
        if (dragKey !== null && dropIndex !== null) persist(reorder(widgets, dragKey, dropIndex))
        resetDrag()
    }
    const onCardDragOver = (e: DragEvent<HTMLElement>, key: string, originalIndex: number) => {
        if (dragKey === null) return
        e.preventDefault()
        if (key === dragKey) return
        const r = e.currentTarget.getBoundingClientRect()
        const after = e.clientY - r.top > r.height / 2
        setDropIndex(originalIndex + (after ? 1 : 0))
    }

    const order = dragKey !== null && dropIndex !== null ? reorder(widgets, dragKey, dropIndex) : widgets
    const stopCustomizing = () => { setCustomizing(false); resetDrag() }

    return (
        <div className={`dash${customizing ? ' customizing' : ''}`}>
            <div className="row" style={{ marginBottom: 18 }}>
                <div className="page-title">
                    <h1>{T.title}</h1>
                    <InfoTip>{T.info}</InfoTip>
                </div>
                <button type="button" onClick={() => setCustomizing(true)}
                    style={{ visibility: customizing ? 'hidden' : 'visible' }}>
                    {T.customize}
                </button>
            </div>

            {widgets.length === 0 ? (
                <p className="muted">
                    {T.noWidgets}
                </p>
            ) : (
                <div className="dash-grid">
                    {order.map((conf) => {
                        const meta = WIDGET_BY_ID[conf.type]
                        if (!meta) return null
                        const originalIndex = widgets.findIndex((w) => w.key === conf.key)
                        const isDragged = dragKey === conf.key
                        const wide = conf.width === 'wide'
                        const Body = meta.Component
                        const title = meta.instanceTitle ? meta.instanceTitle(conf.config) : meta.title
                        return (
                            <section key={conf.key}
                                className={`dash-widget editing${wide ? ' wide' : ''}${isDragged ? ' dragging' : ''}`}
                                draggable
                                onDragStart={(e) => { setDragKey(conf.key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', conf.key) }}
                                onDragOver={(e) => onCardDragOver(e, conf.key, originalIndex)}
                                onDrop={(e) => { if (dragKey !== null) { e.preventDefault(); commitDrop() } }}
                                onDragEnd={resetDrag}
                                aria-hidden={isDragged}
                            >
                                {!isDragged && (
                                    <>
                                        <header className="dash-widget-head">
                                            <span className="dash-drag-handle" title={T.dragToMove} aria-hidden="true">⠿</span>
                                            <h2>{title}</h2>
                                            {/* Always rendered (space reserved); only visibility toggles, so entering
                                                the customize mode doesn't shift the header. */}
                                            <div className="dash-widget-tools" aria-hidden={!customizing}
                                                style={{ visibility: customizing ? 'visible' : 'hidden' }}>
                                                {meta.ConfigEditor && (
                                                    <meta.ConfigEditor config={conf.config ?? {}} onChange={(c) => setConfig(conf.key, c)} />
                                                )}
                                                <button type="button" className="secondary" tabIndex={customizing ? 0 : -1}
                                                    onClick={() => setWidth(conf.key, wide ? 'narrow' : 'wide')}
                                                    title={wide ? T.narrow : T.wide}>
                                                    {wide ? T.narrow : T.wide}
                                                </button>
                                                <button type="button" className="icon-action" title={T.remove} aria-label={T.remove}
                                                    tabIndex={customizing ? 0 : -1} onClick={() => removeWidget(conf.key)}>×</button>
                                            </div>
                                        </header>
                                        <div className="dash-widget-body"><Body config={conf.config} /></div>
                                    </>
                                )}
                            </section>
                        )
                    })}
                </div>
            )}

            <div className="panel" style={{ marginTop: 24 }}>
                <h2>{T.system}</h2>
                {health ? (
                    <p>
                        <span className={`dot ${health.db === 'connected' ? 'ok' : 'bad'}`} />
                        {T.database}<strong>{health.db === 'connected' ? T.connected : T.disconnected}</strong>
                        <span className="muted">{fill(T.statusAt, { time: new Date(health.time).toLocaleString('de-DE') })}</span>
                    </p>
                ) : <p className="muted">{T.loading}</p>}
            </div>

            {/* Customizer drawer (non-modal, so the board stays draggable). */}
            <aside className={`dash-drawer${customizing ? ' open' : ''}`} aria-hidden={!customizing}>
                <header className="dash-drawer-head">
                    <h2>{T.drawerTitle}</h2>
                    <button type="button" className="icon-btn" onClick={stopCustomizing} title={T.close} aria-label={T.close}>×</button>
                </header>
                <p className="muted dash-drawer-hint">{T.drawerHint}</p>
                <div className="dash-drawer-list">
                    {available.length === 0 ? (
                        <p className="muted">{T.noWidgetsAvailable}</p>
                    ) : available.map((w) => (
                        w.configurable ? (
                            <button key={w.id} type="button" className="secondary dash-drawer-add"
                                onClick={() => addInstance(w)}>
                                {fill(T.addWidget, { title: w.title })}
                            </button>
                        ) : (
                            <label key={w.id} className="dash-drawer-item">
                                <input type="checkbox"
                                    checked={widgets.some((x) => x.type === w.id)}
                                    onChange={() => toggleSingle(w.id)} />
                                <span>{w.title}</span>
                            </label>
                        )
                    ))}
                </div>
                <div className="dash-drawer-foot">
                    <button type="button" className="secondary" onClick={reset}>{T.reset}</button>
                </div>
            </aside>
        </div>
    )
}
